import { useMutation, useQuery } from '@tanstack/react-query';
import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';

import { AccountSwitchRow } from '@/components/account/AccountSwitchRow';
import {
  getSharedAccountTextsV2QueryOptions,
  setMySharedAccountTextsV2,
  type SharedAccountTexts,
} from '@/services/sharedAccountTextsV2';
import { queryClient } from '@/util/queryClient';

/**
 * Who is on right now, by first name, so a handoff reads at a glance: an overlap shows two names,
 * a gap shows none. Built from who will actually receive texts, not who flipped the switch, so a
 * person whose texts cannot reach them is never shown as covering.
 */
function whoIsOn(texts: SharedAccountTexts) {
  const names = texts.recipients
    .filter((recipient) => recipient.receiving)
    .map((recipient) => recipient.firstName ?? 'Someone');

  return names.length > 0 ? `On now: ${names.join(', ')}` : 'Nobody is on';
}

/**
 * One support account's texts, switched on or off for the signed-in staff member.
 *
 * Covering a shared inbox such as Ask Anonymously changes hands at least daily, and the person
 * taking over often wants its texts for a while before the handoff. So each person flips their
 * own switch and the texts go to the number on their own account; the shared account's phone is
 * never edited. The switch is disabled while there is no number to text, except to turn it off.
 */
export function SharedAccountTextsRow({
  accountUserId,
  accountLabel,
}: {
  accountUserId: number;
  accountLabel: string;
}) {
  const queryOptions = getSharedAccountTextsV2QueryOptions(accountUserId, true);

  const { data: texts, isLoading, refetch } = useQuery(queryOptions);

  /**
   * Whether the switch can be used depends on the mobile number on the account, which is edited on
   * another screen above this one in the stack. This screen stays mounted underneath it, so coming
   * back would otherwise show the answer from before the number was added.
   */
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const { mutate } = useMutation({
    mutationFn: (enabled: boolean) => setMySharedAccountTextsV2(accountUserId, enabled),
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });

      const previous = queryClient.getQueryData(queryOptions.queryKey);

      queryClient.setQueryData(queryOptions.queryKey, (old) =>
        old ? { ...old, me: { ...old.me, enabled } } : old
      );

      return { previous };
    },
    onError: (_error, _enabled, context) => {
      queryClient.setQueryData(queryOptions.queryKey, context?.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryOptions.queryKey, updated);
    },
  });

  const enabled = texts?.me.enabled ?? false;

  const hasMobilePhone = texts?.me.hasMobilePhone ?? false;

  const getSubtitle = () => {
    if (!texts) {
      return isLoading ? 'Loading…' : 'Unavailable right now';
    }

    if (!hasMobilePhone) {
      return 'Add a mobile number to your account to get texts';
    }

    return whoIsOn(texts);
  };

  return (
    <AccountSwitchRow
      title={`${accountLabel} texts`}
      subtitle={getSubtitle()}
      value={enabled}
      disabled={!texts || (!hasMobilePhone && !enabled)}
      onValueChange={(value) => mutate(value)}
    />
  );
}
